/**
 * @Component - RadioItem (fds-radio-item)
 * @description - RadioItem component for FormDS Web Components
 * ONLY IN USE WITHIN THE RADIO COMPONENT FOR A SINGLE RADIO BUTTON.
 */
import { Component, Element, Event, EventEmitter, h, Method, Prop, State } from '@stencil/core';
import clsx from 'clsx';

@Component({
  tag: 'fds-radio-item',
  styleUrl: 'styles/radio-item.scss',
  scoped: true,
})
export class RadioItem {
  @Element() el!: HTMLFdsRadioItemElement;

  /**
   * @internal
   */
  @State() radioTabIndex = -1;

  /**
   * @internal
   */
  @State() isFocused = false;

  /**
   * @internal
   */
  @Prop() name!: string;

  /**
   * @internal
   */
  @Prop() isChecked: boolean;

  @Prop() value!: string;

  @Prop() disabled = false;

  @Event() fdsRadioChecked: EventEmitter<string>;

  @Method()
  async setTabIndex(tabIndex: number) {
    this.radioTabIndex = tabIndex;
  }

  @Method()
  async setFocus() {
    (this.el.querySelector('input[type="radio"]') as HTMLInputElement).focus();
  }

  private emitIfNotDisabled = () => {
    if (!this.disabled) {
      this.fdsRadioChecked.emit(this.value);
    }
  };

  @Method()
  async emitChecked() {
    this.emitIfNotDisabled();
  }

  private onFocus = () => {
    this.isFocused = true;
  };

  private onBlur = () => {
    this.isFocused = false;
  };

  private handleClick = () => {
    this.emitIfNotDisabled();
  };

  private handleKeydown = (event: KeyboardEvent) => {
    if (event.key === ' ') {
      event.preventDefault();
      this.emitIfNotDisabled();
    }
  };

  render() {
    const { disabled, isChecked, radioTabIndex, value, name, handleClick, handleKeydown, onFocus, onBlur, isFocused } =
      this;
    return (
      <label
        class={clsx('fds-radio-item', {
          'fds-radio-item--disabled': disabled,
          'fds-radio-item--focused': isFocused,
        })}
      >
        <input
          class="fds-radio-item__input"
          type="radio"
          value={value}
          tabIndex={radioTabIndex}
          checked={isChecked}
          name={name}
          disabled={disabled}
          onClick={handleClick}
          onKeyDown={handleKeydown}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <fds-icon name={isChecked ? 'radio_button_checked' : 'radio_button_unchecked'} />
        <span class="fds-radio-item__label">
          <slot></slot>
        </span>
      </label>
    );
  }
}
